import React, { useCallback, cloneElement } from 'react';
import { cn } from 'utils/classnames';
import { FromDesktop } from 'common/components/adaptive/desktop';
import { CarouselControl } from './styles';
import { ProductsCarouselControl } from 'features/products/components/products-carousel/products-carousel-control';
import { useScrollSponsoredCarousel } from 'features/sponsored-carousel/hooks';
import type { CarouselVariant } from './types';
import { RootContainer, rootWrapperStyles } from './variants/root';
import {
  SponsoredContainer,
  sponsoredWrapperStyles,
  sponsoredWrapperStylesWithVisibleOverflow,
} from './variants/sponsored';
import { useCarouselLogic } from './hooks/use-carousel-logic';
import { cx } from 'linaria';

type CarouselProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> & {
  buttonTitle?: {
    left: string;
    right: string;
  };
  canScrollCallBack?: (left?: boolean, right?: boolean) => void;
  controlLeft?: JSX.Element;
  controlRight?: JSX.Element;
  showControlsEveryTime?: boolean;
  controlsClassName?: string;
  scrollStep?: number;
  slWidgetId?: string;
  onChange?: (value: number) => void;
  variant?: CarouselVariant;
  innerContainerClassName?: string;
  carouselId?: string;
  shouldDisableCarouselClipping?: boolean;
};

export const Carousel: React.FC<CarouselProps> = ({
  children,
  className,
  controlsClassName,
  buttonTitle = { left: 'Предыдущий слайд', right: 'Следующий слайд' },
  canScrollCallBack,
  controlLeft,
  controlRight,
  showControlsEveryTime = false,
  scrollStep,
  slWidgetId,
  onChange,
  variant = 'root',
  innerContainerClassName,
  carouselId,
  shouldDisableCarouselClipping,
  ...props
}) => {
  const indentation = variant === 'sponsored' ? 50 : 150;

  const { containerRef, inScrollNow, onControlClick, onScroll, canScrollLeft, canScrollRight } =
    useCarouselLogic({
      canScrollCallBack,
      scrollStep,
      onChange,
      indentation,
    });

  useScrollSponsoredCarousel(canScrollRight, onControlClick, carouselId);

  const setVariantWrapperStyles = useCallback(() => {
    switch (variant) {
      case 'sponsored':
        return cx(
          sponsoredWrapperStyles,
          shouldDisableCarouselClipping && sponsoredWrapperStylesWithVisibleOverflow,
        );
      case 'root':
      default:
        return rootWrapperStyles;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant]);

  const setVariantContainer = () => {
    switch (variant) {
      case 'sponsored':
        return (
          <SponsoredContainer
            className={innerContainerClassName}
            ref={containerRef}
            onScroll={onScroll}
            inScrollNow={inScrollNow}
            shouldDisableCarouselClipping={shouldDisableCarouselClipping}
            data-sl-widget-id={slWidgetId}
            data-sl-external="true"
            data-qa-carousel=""
            data-qa="carousel"
          >
            {children}
          </SponsoredContainer>
        );
      case 'root':
      default:
        return (
          <RootContainer
            className={cx(innerContainerClassName, 'global-carousel-root-container')}
            ref={containerRef}
            onScroll={onScroll}
            inScrollNow={inScrollNow}
            data-sl-widget-id={slWidgetId}
            data-sl-external="true"
            data-qa-carousel=""
            data-qa="carousel"
          >
            {children}
          </RootContainer>
        );
    }
  };

  const getControlContent = useCallback(
    (direction: 'left' | 'right' = 'left') => {
      const dataQAAttr = `carousel-control-${direction}`;

      const data = {
        direction,
        onClick: () => onControlClick(direction),
        title: buttonTitle?.[direction],
        'data-qa': dataQAAttr,
      };

      if (controlLeft && direction === 'left') {
        return cloneElement(controlLeft, data);
      }

      if (controlRight && direction === 'right') {
        return cloneElement(controlRight, data);
      }

      return (
        <ProductsCarouselControl direction={data.direction} onClick={data.onClick} data-qa={dataQAAttr} />
      );
    },
    [buttonTitle, controlLeft, controlRight, onControlClick],
  );

  return (
    <div className={cn(className, setVariantWrapperStyles())} {...props}>
      <FromDesktop>
        {(showControlsEveryTime || canScrollLeft) && (
          <CarouselControl direction="left" data-qa-carousel-control-wrapper-left="">
            {getControlContent('left')}
          </CarouselControl>
        )}
      </FromDesktop>
      {setVariantContainer()}
      <FromDesktop>
        {(showControlsEveryTime || canScrollRight) && (
          <CarouselControl direction="right" data-qa-carousel-control-wrapper-right="">
            {getControlContent('right')}
          </CarouselControl>
        )}
      </FromDesktop>
    </div>
  );
};
